import React, { useState } from 'react'
import themeStyles from './styles'
import {
  VictoryChart,
  VictoryZoomContainer,
  VictoryLine,
  VictoryArea,
  VictoryScatter,
  VictoryTooltip,
  VictoryAxis,
  VictoryContainer
} from 'victory'
import { useTheme } from 'contexts/ThemeProvider'
import _ from 'lodash'

const INITIAL_LENGTH = 10

const TrendLinePC = ({ initialData }) => {
  const { theme: Colors } = useTheme()
  const styles = themeStyles(Colors)
  const dataLength = Math.min(initialData?.length, INITIAL_LENGTH)
  const [activeIndex, setActiveIndex] = useState(null)

  // Define normal range area
  const minLow = _.minBy(initialData, 'low')?.low // min value of normal range Area
  const maxHigh = _.maxBy(initialData, 'high')?.high // max value of normal range Area
  const range = maxHigh - minLow

  // Define default axisY
  const minValues = initialData?.map(item =>
    Math.min(
      _.defaultTo(item.result, Infinity),
      _.defaultTo(item.low, Infinity)
    )
  )
  const minAxisY = Math.max(_.min(minValues) - range, 0)
  const maxValues = initialData.map(item =>
    Math.max(
      _.defaultTo(item.result, -Infinity),
      _.defaultTo(item.high, -Infinity)
    )
  )
  const maxAxisY = Math.min(_.max(maxValues) + range)

  // Define data
  const groupedData = _.groupBy(initialData, 'date')
  const deDupData = _.flatMap(groupedData, (group, date) => {
    if (group?.length > 1) {
      return group.map((item, index) => {
        return { ...item, x: `${date}_${index}`, displayDate: date }
      })
    }
    return group.map(item => ({ ...item, x: item.date, displayDate: item.date }))
  })

  const allValueZero = _.every(deDupData, { result: 0 })

  const transData = deDupData.map(item => ({
    x: item?.x,
    y: item?.result,
    displayDate: item?.displayDate
  }))

  const domain = {
    x: [0.5, 1 + dataLength],
    y: [minAxisY, maxAxisY]
  }
  const data = _.take(transData, dataLength)

  const getAreaData = num => {
    const arr = []
    for (let i = 0; i < num; i++) {
      arr.push({ x: i, y: maxHigh, y0: minLow })
    }
    return arr
  }
  const areaData = getAreaData(dataLength + 1)

  return (
    <div style={styles.container}>
      <VictoryChart
        width={600}
        height={300}
        padding={{ left: 50, bottom: 40, top: 20 }}
        containerComponent={
          (deDupData?.length > 0 && !allValueZero)
            ? <VictoryZoomContainer
                minimumZoom={{ x: 1, y: range / 3 }}
                allowPan
                // Prevent the active tooltip to be out of range
                onZoomDomainChange={(domain) => {
                  if (activeIndex !== null) {
                    const inView =
                      activeIndex + 1 > _.get(domain, 'x[0]') &&
                      activeIndex + 1 < _.get(domain, 'x[1]') &&
                      _.get(data, `[${activeIndex}].y`) > _.get(domain, 'y[0]') &&
                      _.get(data, `[${activeIndex}].y`) < _.get(domain, 'y[1]')
                    if (!inView) {
                      setActiveIndex(null)
                    }
                  }
                }}
              />
            : <VictoryContainer />
        }
        events={[
          {
            target: 'parent',
            eventHandlers: {
              onClick: (evt) => {
                evt.stopPropagation()
                return [{ mutation: () => setActiveIndex(null) }]
              }
            }
          }
        ]}
      >
        <VictoryAxis
          crossAxis
          style={styles.axis(dataLength)}
          standalone={false}
          tickFormat={(t) => {
            const matchedData = transData.find(item => item.x === t)
            return matchedData ? matchedData.displayDate : t
          }}
        />
        <VictoryAxis
          dependentAxis
          crossAxis
          style={styles.axis(dataLength)}
          standalone={false}
        />
        <VictoryArea style={styles.area} theme={styles} data={areaData} />
        <VictoryLine
          style={styles.line}
          data={data}
          theme={styles}
          domain={{ ...domain }}
        />
        <VictoryScatter
          style={{
            ...styles.scatter,
            data: {
              fill: ({ datum, index }) =>
                datum.y >= _.get(initialData, `[${index}].low`) && datum.y <= _.get(initialData, `[${index}].high`)
                  ? Colors.inRangeGreen
                  : Colors.outRangeYellow
            }
          }}
          labels={({ datum }) => datum.y}
          theme={styles}
          events={[
            {
              target: 'data',
              eventHandlers: {
                onClick: (evt, { index }) => {
                  evt.stopPropagation()
                  return [{ mutation: () => setActiveIndex(index) }]
                },
                onMouseOver: () => ({
                  target: 'labels',
                  mutation: () => {}
                })
              }
            }
          ]}
          labelComponent={
            <VictoryTooltip
              flyoutStyle={styles.flyoutStyle}
              renderInPortal={false}
              pointerLength={15}
              active={({ index }) => index === activeIndex}
            />
          }
          size={7}
          data={data}
        />
      </VictoryChart>
    </div>
  )
}

export default TrendLinePC
